// Implements: F-UIX-019, F-IDN-027
/**
 * `/plataforma/organizaciones`: Plataforma > Organizaciones (mockup v3 P3). The page for whoever the
 * server lets set an organization's status; the not-found page for anybody else (the other
 * platform pages follow the same rule).
 *
 * The page composes two modules: the organizations and their writes (`organization`), and the
 * month's spend per organization from the platform totals (`admin`), read for the current month in
 * the reader's zone, formatted by the admin module (OD-12) and handed in as text, every currency the
 * organization spent in. A failed or slow totals read only leaves the spend column at "—".
 */

import { useMemo, useState } from "react";
import type { ReactElement } from "react";

import { usePlatformTotals } from "../../modules/admin/hooks/usePlatformTotals";
import { platformAllows } from "../../modules/admin/model/platformAccess";
import { organizationSpend } from "../../modules/admin/model/platformRows";
import { PlatformOrganizationsContainer } from "../../modules/organization/containers/PlatformOrganizationsContainer";
import { monthToDate } from "../../modules/organization/model/month";
import { useSession } from "../../shared/api/session";
import { useTranslation } from "../../shared/i18n";
import { PageHeader } from "../../shared/ui/molecules/PageHeader";
import { NotFoundPage } from "./NotFoundPage";

function OrganizationsWithSpend(): ReactElement {
  const [range] = useState(() => monthToDate(new Date()));
  const totals = usePlatformTotals(range);
  const spend = useMemo(() => (totals.data === undefined ? null : organizationSpend(totals.data)), [totals.data]);
  return <PlatformOrganizationsContainer spend={spend} />;
}

export function PlatformOrganizationsPage(): ReactElement {
  const { t } = useTranslation();
  const { state } = useSession();
  const scopes = state.status === "authenticated" ? state.view.scopes : [];
  if (!platformAllows(scopes, "organization:set_status")) return <NotFoundPage />;
  return (
    <section>
      <PageHeader title={t("organization.platform.title")} subtitle={t("organization.platform.intro")} />
      <OrganizationsWithSpend />
    </section>
  );
}
